/**
 * BuilderPreview.
 *
 * Section 4 of the Recurring Builder drawer. Shows the user a live
 * summary of what will happen when they Save, so they can sanity-check
 * before committing:
 *   - One-sentence prose summary of the schedule + transaction
 *   - The next 3 concrete occurrence dates
 *
 * All derivations run through pure helpers in builderUtils, so this
 * component is purely presentational. Updates live as the user edits
 * any field in sections 1 through 3 (React re-renders whenever the
 * draft changes in the parent hook).
 */

import { useMemo, type CSSProperties } from "react";
import { BuilderSection } from "./RecurringBuilder";
import {
  formatPreviewSummary,
  computeNextOccurrences,
} from "./builderUtils";
import { formatDateLong } from "../recurringUtils";
import type { BuilderDraft } from "./builderTypes";

export interface BuilderPreviewProps {
  draft: BuilderDraft;
}

export function BuilderPreview({ draft }: BuilderPreviewProps) {
  const summary = useMemo(() => formatPreviewSummary(draft), [draft]);
  const nextDates = useMemo(() => computeNextOccurrences(draft, 3), [draft]);

  // Weekday short name for the chip labels (e.g., "Sun, Nov 1, 2026").
  const formatOccurrence = (iso: string): string => {
    const d = new Date(iso);
    const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
    return `${dow}, ${formatDateLong(iso)}`;
  };

  return (
    <BuilderSection number={4} title="Preview">
      <div style={summaryStyle}>{summary}</div>

      <div style={chipsStyle}>
        {nextDates.length === 0 ? (
          <div style={emptyStyle}>No occurrences will fire with the current settings.</div>
        ) : (
          nextDates.map((iso, i) => (
            <div key={iso} style={chipStyle}>
              <div style={chipLabelStyle}>{i === 0 ? "Next" : "Then"}</div>
              <div style={chipDateStyle}>{formatOccurrence(iso)}</div>
            </div>
          ))
        )}
      </div>
    </BuilderSection>
  );
}

// ---------- Styles ----------

const summaryStyle: CSSProperties = {
  padding: "14px 16px",
  background: "#F3F5F5",
  borderRadius: 8,
  fontSize: 13,
  color: "#263238",
  lineHeight: 1.6,
  marginBottom: 14,
};

const chipsStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const chipStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 12px",
  background: "#ffffff",
  border: "1px solid #E7EAF0",
  borderRadius: 6,
  fontSize: 12,
};

const chipLabelStyle: CSSProperties = {
  color: "#3A4A52",
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontSize: 10.5,
};

const chipDateStyle: CSSProperties = {
  color: "#0E1A1A",
  fontWeight: 600,
};

const emptyStyle: CSSProperties = {
  padding: "12px",
  fontSize: 12,
  color: "#6B7684",
  fontStyle: "italic",
  textAlign: "center",
};

export default BuilderPreview;